<template>
  <v-sheet>
    <v-form ref="frmSectionEditor" @submit.prevent="submitForm">
      <v-container fluid>
        <v-row no-gutters>
          <v-col cols="12">
            <v-row no-gutters>
              <v-col v-if="isEnduroSprintMode" cols="6" md="2" class="ml-auto max-width-run-limit">
                <v-text-field
                  v-model="runLimit"
                  :label="$t('page.sectionEditor.max_repeat_limit')"
                  hide-details="auto"
                  outlined
                  dense
                  type="number"
                  min="1"
                  style="max-width: 160px"
                  persistent-hint
                  @input="validateInput"
                  :hint="$t('page.sectionEditor.minimun_value')"
                  :rules="[ (v) => $inputRules({ value: v }, 'filled') || $t('page.sectionEditor.run-limit-required') ]"
                ></v-text-field>
              </v-col>
              <v-col v-if="isModeRestart" cols="6" sm="4" class="ml-auto">
                <v-switch compact dense
                  hide-details="auto"
                  color="#F00"
                  class="includeFlagSwitch pb-3 px-6"
                  name="isSpeedLimit"
                  v-model="isSpeedLimitLocal"
                  :true-value="1"
                  :false-value="0"
                  :label="$t('page.sectionEditor.enforce_average_speed')"
                  @change="updateField('isSpeedLimit', $event)"
                ></v-switch>
              </v-col>
              <v-col v-if="isEnduroSprintMode" cols="6" md="3" class="max-width-time-trial">
                <v-switch compact dense
                  hide-details="auto"
                  color="#F00"
                  class="includeFlagSwitch pb-3 px-6"
                  name="isTimeTrial"
                  v-model="isTimeTrial"
                  :input-value="isTimeTrialInitial"
                  :true-value="1"
                  :false-value="0"
                  :label="$t('page.sectionEditor.time_trial')"
                ></v-switch>
              </v-col>
              <v-col cols="6" lg="3" :md="colMdIncludeFlag" :sm="smNroColDisplayedFlag" :class="`max-width-include-flag ${isModeReliabilityEnduro ? 'ml-auto' : ''}`">
                <v-switch compact dense
                  hide-details="auto"
                  color="#F00"
                  class="includeFlagSwitch pb-3 px-6"
                  name="includeFlag"
                  v-model="includeFlagLocal"
                  :true-value="0"
                  :false-value="1"
                  :label="$t('page.sectionEditor.throwwholesection')"
                ></v-switch>
              </v-col>
              <v-col v-if="isDisplayedFlag" cols="6" lg="3" md="3" :sm="smNroColDisplayedFlag" :class="`max-width-hide-thrown-section${nroColumnsInFirstRow > 3 ? ' ml-sm-auto ml-md-0' : ''}`">
                <v-switch 
                  compact dense
                  hide-details="auto"
                  color="#F00"
                  class="includeFlagSwitch pb-3 px-6"
                  name="isExcludedDisplayed"
                  :input-value="SelectedSection.isExcludedDisplayed"
                  :true-value="0"
                  :false-value="1"
                  :label="$t('page.sectionEditor.hide_thrown_section')"
                  @change="updateField('isExcludedDisplayed', $event)"
                ></v-switch>
              </v-col>
              <v-col 
                cols="6" 
                v-if="isOptionHideSection || isModeReliabilityEnduro"
                :class="`max-width-hide-section ${classesHideSection}`" 
              >
                <v-switch compact dense
                  hide-details="auto"
                  color="#F00"
                  class="includeFlagSwitch pb-3 px-6"
                  :input-value="SelectedSection.isHidden"
                  :true-value="1"
                  :false-value="0"
                  :label="$t('page.sectionEditor.hide_section')"
                  @change="updateField('isHidden', $event)"
                ></v-switch>
              </v-col>
            </v-row>
            <v-row no-gutters v-if="isOptionHideSection && !isModeReliabilityEnduro">
              <v-col 
                cols="6" md="3" sm="4" 
                :class="`max-width-hide-section ml-auto ${nroColumnsInFirstRow > 3 ? 'd-sm-none d-md-block' : 'd-none d-sm-block'}`"
              >
                <v-switch compact dense
                  hide-details="auto"
                  color="#F00"
                  class="includeFlagSwitch pb-3 px-6"
                  :input-value="SelectedSection.isHidden"
                  :true-value="1"
                  :false-value="0"
                  :label="$t('page.sectionEditor.hide_section')"
                  @change="updateField('isHidden', $event)"
                ></v-switch>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row class="text-right">
          <v-col cols="12">
            <v-btn @click="cloneSection" small class="mr-6">
              <v-icon left dark>fas fa-clone</v-icon>
              {{ $t('page.sectionEditor.clone_section', 1) }}
            </v-btn>
            <v-btn @click="deleteSectionConfirm" small>
              <v-icon left dark>fas fa-trash</v-icon>
              {{ $t('page.sectionEditor.delete_section', 1) }}
            </v-btn> 
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="auto" sm="4">
            <v-text-field
              hide-details="auto"
              :label="$t('page.sectionEditor.section_name')"
              outlined
              dense
              name="sectionName"
              @input="updateField('sectionName', $event)"
              :value="SelectedSection.sectionName"
              :rules="[ (v) => $inputRules({ value: (v || '') }, 'filled') || $t('page.sectionEditor.errors.section-name-required') ]"
            ></v-text-field>
          </v-col>
          <v-col cols="auto" sm="2">
            <v-text-field
              ref="sectionLengthInput"
              hide-details="auto"
              :label="$t('page.sectionEditor.section_length')"
              outlined
              dense
              name="sectionLength"
              :value="SelectedSection.length"
              :rules="inputLengthRules"
              @input="updateSectionLength($event)"
            ></v-text-field>
          </v-col>
          <v-col cols="auto" sm="2" v-if="isModeRestart">
            <v-text-field
              ref="sectionExpectedSpeedInput"
              hide-details="auto"
              :label="$t('page.sectionEditor.average_speed')"
              outlined
              dense
              name="expectedSpeed "
              :value="SelectedSection.expectedSpeed"
              :rules="inputExpectedSpeedRules"
              @input="updateExpectedSpeed($event)"
            >
            <template v-slot:append>
              <span style="margin-top: 4px;">
                {{ measure }}
              </span>
            </template>
          </v-text-field>
          </v-col>
          <v-col cols="auto" sm="4">
            <v-select
              hide-details="auto"
              name="sectionMeasure"
              :value="SelectedSection.measure"
              :items="raceLengths"
              item-text="txt"
              item-value="val"
              dense
              outlined
              @change="setMeasure"
              @input="updateField('measure', $event)"
              :label="$t('page.sectionEditor.length_measure')"
              :rules="[ (value) => $inputRules({ value: (value || '') }, 'filled') || $t('page.sectionEditor.errors.length-measure-required') ]"
              :no-data-text="$t('common.no_data')"
            ></v-select>
          </v-col>
        </v-row>
      </v-container>

      <v-card class="mx-auto" width="100%" outlined>
        <v-expansion-panels focusable hover multiple class="pb-4" v-model="expandedPanels">
          <v-expansion-panel
            v-for="check in this.SelectedSection.raceSectionChecks"
            :key="check.id"
          >
            <v-expansion-panel-header class="justify-self-start">
              <div>
                <v-icon>{{ getCheckIcon(check.checkType) }}</v-icon>&nbsp;&nbsp;&nbsp;
                <span>{{ getHeaderCheckText(check)  }}</span>
              </div>
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <SectionChecks 
                ref="sectionCheck" 
                :Check="check" 
                :Section="SelectedSection" 
                :runLimit="runLimit" 
              />
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-header>{{ $t('page.sectionEditor.thrown_classes') }}</v-expansion-panel-header>

            <v-expansion-panel-content>
              <SectionClasses 
                ref="sectionClasses" 
                :Section="SelectedSection" 
                :RaceId="RaceId"
                :runLimit="runLimit" 
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card>
    </v-form>

  <SGSNotificationConfirm v-if="dialog" :notificationMessage="$t('page.sectionEditor.delete-confirmation-message')" @cancel="dialog=false;" @agree="deleteSection()" type='warning'/>
  <SGSNotificationConfirm v-if="dialogUpdate" :notificationMessage="$t('page.sectionEditor.update-confirmation-message')" @cancel="updateFieldConfirm('no')" @agree="updateFieldConfirm('yes')" type='warning'/>
  <SGSNotificationAlert v-if="errorDialog" :notificationMessage="$t(`page.sectionEditor.${errorDialogMessage}`)" @cancel="errorDialog=false;" type='error'/>

  </v-sheet>
</template>

<script>
import { apiCalls } from "@/mixins/apiCalls.js";
import SectionChecks from "@/components/section-editor/SectionChecks.vue";
import SectionClasses from "@/components/section-editor/SectionClasses.vue";
import SGSNotificationConfirm from "@/components/custom/notification/NotificationConfirm.vue";
import SGSNotificationAlert from "@/components/custom/notification/NotificationAlert.vue";
import { mapState } from 'vuex';

export default {
  mixins: [apiCalls], //for api redundant operations
  props: ["SelectedSection", "RaceId"],
  components: {
    SectionChecks,
    SectionClasses,
    SGSNotificationConfirm,
    SGSNotificationAlert
  },
  data() {
    return {
      sectionDetailsAPIUrl: '/api/v1/promoter/section',
      raceLengths: [
        {
          val: "kms",
          txt: this.$t("page.sectionEditor.kms")
        },
        {
          val: "miles",
          txt: this.$t("page.sectionEditor.miles")
        }
      ],
      tab: null,
      items: [
        {
          id: "checks",
          title: this.$t("page.sectionEditor.check")
        },
        {
          id: "classes",
          title: this.$t("page.sectionEditor.class_handler")
        }
      ],
      dialog: false,
      errorDialog: false,
      errorDialogMessage: '',
      VUE_APP_HEADER_COLOR: process.env.VUE_APP_HEADER_COLOR,
      runLimit: null,
      dialogUpdate: false,
      itemUpdate: null,
      bStart: false,
      expandedPanels: [],
      readOnly: false,
      measure: ''
    };
  },
  computed: {
    colMdIncludeFlag() {
      return this.isDisplayedFlag ? 3 : 4;
    },
    nroColumnsInFirstRow() {
      if(this.isEnduroSprintMode) {
        return this.isDisplayedFlag ? 4 : 3;
      }
      if(this.isModeRestart) {
        return this.isDisplayedFlag ? 2 : 3;
      }
      return 0;
    },
    smNroColDisplayedFlag() {
      if(this.isEnduroSprintMode) return 6;
      return 4;
    },
    isDisplayedFlag() {
      return Number(this.SelectedSection?.includeFlag) === 0;
    },
    isOptionHideSection() {
      return this.Race?.mode == 10 || this.Race?.mode == 11;
    },
    isModeReliabilityEnduro(){
      return this.Race?.mode == 10 && this.Race?.subMode == 'reliability';
    },
    classesHideSection() {
      if(this.isModeReliabilityEnduro) return '';
      return this.nroColumnsInFirstRow > 3 ? 'd-cs-hide-4 d-cs-none' : 'd-sm-none';
    },
    ...mapState({
      clonnedSection: state => state.sectionEditor.clonnedSection
    }),
    isEnduroSprintMode() {
      return this.Race && this.Race.mode == 10 && this.Race.subMode == 'sprint';
    },
    isModeRestart(){
      return this.Race && this.Race.mode == 10 && this.Race.subMode == 'restart';
    },
    includeFlag() {
      return this.SelectedSection.includeFlag;
    },
    includeFlagLocal: {
      get() {
        return Number(this.SelectedSection?.includeFlag ?? 1);
      },
      set(value) {
        const valNum = Number(value);
        this.$set(this.SelectedSection, 'includeFlag', valNum);
        this.updateField('includeFlag', valNum);
      }
    },
    inputLengthRules() {
      return [
        (value) => this.$inputRules({ value: (value || '') }, 'filled') || this.$t('page.sectionEditor.section_length_required'),
        (value) => !this.$inputRules({ value }, 'filled') || (
            this.$inputRules({ value }, 'filled') && (
              this.$inputRules({ value }, 'integer') || this.$inputRules({ value }, 'floatTwoDecimal')
            )
          ) || this.$t('page.sectionEditor.section_length_invalid')
      ]
    },
    isSpeedLimitLocal: {
      get() {
        return this.SelectedSection?.isSpeedLimit ?? 0;
      },
      set(value) {
        const valNum = Number(value);
        this.$set(this.SelectedSection, 'isSpeedLimit', valNum);
        this.updateField('isSpeedLimit', valNum);
        if (valNum !== 1 && this.$refs.sectionExpectedSpeedInput) {
          this.$refs.sectionExpectedSpeedInput.resetValidation();
        }
      }
    },
    inputExpectedSpeedRules() {
      if (this.SelectedSection.isSpeedLimit === 1) {
        return [
          (value) => this.$inputRules({ value: (value || '') }, 'filled') || this.$t('page.sectionEditor.average_speed_required'),
          (value) => !this.$inputRules({ value }, 'filled') || (
            this.$inputRules({ value }, 'filled') && this.$inputRules({ value }, 'floatTwoDecimal')
          ) || this.$t('page.sectionEditor.average_speed_invalid'),
          (value) => Number.isInteger(+value) && +value > 0 || this.$t('page.sectionEditor.average_speed_invalid')
        ];
      }
      return [ (/* value */) => true ];
    },
    Race() {
      return this.$store.getters.getRaceSections;
    },
    RaceClasses() {
      return this.$store.getters.getClasses;
    },
    isReadOnly() {
      return this.SelectedSection.isTimeTrial == 1;
    },
    isTimeTrialInitial: () => this?.SelectedSection?.isTimeTrial,
    isTimeTrial: {
      get() {
        return this?.SelectedSection?.isTimeTrial;
      }, 
      set(value) {
        this.updateField('isTimeTrial', value);
    }
    }
  },
  methods: {
    setMeasure(event) {
      this.measure = event;
    },
    validateInput(){
      if(this.runLimit < 1 || this.runLimit === null || this.runLimit === ''){
        this.runLimit = 1;
      }
    },
    deleteSectionConfirm(){
      this.dialog = true
    },
    deleteSection: async function() {
      this.dialog = false

      let formData = {
        raceSectionId: this.SelectedSection.id,
        raceId: this.SelectedSection.raceId
      };

      const data = await this.api('delete', '/api/v1/promoter/section', formData);
      
      if(typeof data == 'undefined' || (data && typeof data.error == 'undefined')){
        const response = data;
        if (/^-?\d+$/.test(response)) {
          //this.$emit("refreshSectionList", this.SelectedSection.raceId, "last");
          this.$store.dispatch('getSectionsAction',this.RaceId);
        } else {
          if(response){
            this.errorDialogMessage = response;
            this.errorDialog = true;
            //show general alert for this.$t("page.sectionEditor."+response)
          }
        }
      }else{
        this.errorDialogMessage = (data && data.error) ? data.error : (typeof data == 'string' ? data : this.$t('page.sectionEditor.error_general'));
        this.errorDialog = true
      }
    },
    cloneSection: async function() {
      let formData = new FormData();
      formData.append("raceSectionId", this.SelectedSection.id);
      formData.append("raceId", this.SelectedSection.raceId);

      const response = await this.api('post', '/api/v1/promoter/section', formData);
      if (response) {
        //this.$emit("refreshSectionList", this.SelectedSection.raceId, "last");
        this.$store.dispatch('getSectionsAction',this.RaceId);
      }

      this.$store.commit('updateClonnedSection')
    },
    handleTimeTrialChange(event) {
      this.updateField('isTimeTrial', event);
    },
    updateField(field, value) {
      this.$store.dispatch('addSectionCheckUpdateAction', { field, value, sectionId: this.SelectedSection.id });
    },
    deleteKeyTime(params) {
      this.$emit("deleteKeyTime", params);
    },
    confirmUpdate(params) {
      if(this.isEnduroSprintMode) {
        this.itemUpdate = params;
        this.dialogUpdate = true;
      }
        this.updateField(params.field, params.value);
    },
    updateFieldConfirm(response) {
      //This is only available when in Enduro Sprint mode
      if(!this.itemUpdate) {
        this.dialogUpdate = false;
        return false;
      }
      let applyAll = response == 'yes' ? 1 : 0;
      this.dialogUpdate = false;
      if(this.$refs.sebctionClasses) {
        this.$refs.sectionClasses.updateRunLimit(applyAll);
      } else {
        this.updateRunLimit(applyAll);
      }
    },
    updateRunLimit(applyAll) {
        //This is only available when in Enduro Sprint mode
        let raceSectionClass = [];
        if(applyAll) {
          raceSectionClass = this.SelectedSection.raceSectionClass.map(el => ({...el}));
          this.RaceClasses.forEach((classItem) => {
            let findIndex = raceSectionClass.findIndex((el) => el.fkClassId == classItem.id);
            if(findIndex >= 0) {
              raceSectionClass[findIndex].runLimit = parseInt(this.runLimit);
            } else {
                raceSectionClass.push({
                    fkClassId: classItem.id, 
                    isExcluded: 0, 
                    runLimit: parseInt(this.runLimit)
                });
            }
          });
        } else {
          raceSectionClass = this.SelectedSection.raceSectionClass.map(el => ({...el}));
          this.RaceClasses.forEach((classItem) => {
            let findIndex = raceSectionClass.findIndex((el) => el.fkClassId == classItem.id);
            if(findIndex >= 0) {
              if(raceSectionClass[findIndex].runLimit > this.runLimit) {
                raceSectionClass[findIndex].runLimit = parseInt(this.runLimit);
              }
            } else if(classItem.runLimit > this.runLimit) {
                raceSectionClass.push({
                    fkClassId: classItem.id, 
                    isExcluded: 0, 
                    runLimit: parseInt(this.runLimit)
                });
            }
          });
        }
        this.$store.dispatch('addSectionCheckUpdateAction', { 
          field: 'raceSectionClass', 
          value: raceSectionClass, 
          sectionId: this.SelectedSection.id 
        });
    },
    updateSectionLength(newValue) {
      if(!this.$refs.sectionLengthInput.validate(true)){
        return;
      }
      this.updateField('length',newValue);
    },
    updateExpectedSpeed(newValue) {
      if(!this.$refs.sectionExpectedSpeedInput.validate(true)){
        return;
      }
      this.updateField('expectedSpeed',newValue);
    },
    submitForm() {},
    getHeaderCheckText(check) {
      let title = '';
      switch(check.checkType) {
        case "in": 
          title = this.$t('page.sectionEditor.checkin_headerp1') + check.checkNumber + this.$t('page.sectionEditor.checkin_headerp2');
        break;
        case "out": 
          title = this.$t('page.sectionEditor.checkout_headerp1', 1) + check.checkNumber + this.$t('page.sectionEditor.checkout_headerp2', 1);
        break;
      }
      return title;
    },
    getCheckIcon(checkType) {
      return checkType == "in" ? "fa-arrow-circle-right" : "fa-arrow-circle-left";
    }
  },
  mounted() {
    this.measure = this.SelectedSection.measure;
    this.runLimit = this.SelectedSection.runLimit;
  },
  watch: {
    includeFlag(newVal, oldVal){
      if(newVal == 1 && this.SelectedSection.isExcludedDisplayed == oldVal){
        this.updateField('isExcludedDisplayed', newVal);
        this.SelectedSection.isExcludedDisplayed = newVal;
      }
    },
    errorDialog() {
      this.dialogUpdate = false;
    },
    runLimit(newVal,oldVal) {
      if(this.bStart) {
        this.confirmUpdate({ field: 'runLimit', value: newVal, oldValue: oldVal });
      } else {
        this.bStart = true;
    }
  }
  }
};
</script>

<style lang="scss">
.includeFlagSwitch.v-input--is-label-active .theme--light.v-label {
  color: #f00 !important;
}

.cardTop {
    /*background-color: #a60606;*/
    height: 8px;
    margin-bottom: 5px;
}

i.delete {
    color: #883c0e;
    font-size: 50px;
    vertical-align: middle;
    position: absolute;
    left: 10px;
}

div.delete-text {
    line-height: 18px;
    padding-left: 50px;
    text-align: left;
}

.d-cs-none {
  display: none !important;
}

@media screen and (min-width: 599px) {
  .max-width-run-limit {
    max-width: 150px !important;
  }
  .max-width-time-trial {
    max-width: 200px !important;
  }
  .max-width-include-flag {
    max-width: 280px !important;
  }
  .max-width-hide-thrown-section {
    max-width: 280px !important;
  }
  .max-width-hide-section {
    max-width: 220px !important;
  }
}

@media screen and (min-width: 600px) and (max-width: 959px) {
  .max-width-run-limit {
    max-width: 120px !important;
  }
  .max-width-time-trial {
    max-width: 165px !important;
  }
  .max-width-include-flag {
    max-width: 245px !important;
  }
  .max-width-hide-thrown-section {
    max-width: 245px !important;
  }
  .max-width-hide-section {
    max-width: 180px !important;
  }
}

@media screen and (min-width: 600px) and (max-width: 921px) {
  .d-cs-hide-4 {
    display: block !important;
  }
}
@media screen and (min-width: 600px) and (max-width: 959px) {
  .ml-cs-hide {
    margin-left: auto !important;
  }
}
@media screen and (min-width: 750px) and (max-width: 920px) {
  .ml-cs-hide {
    margin-left: 0px !important;
  }
}
</style>